/* eslint-disable @typescript-eslint/indent */
import React, { Dispatch, SetStateAction, useMemo } from 'react';
import Image from 'next/legacy/image';
import ImageLoaded from '@components/common/ImageLoaded/ImageLoaded';
import Product from '@customTypes/product';
import { CartItemDetails } from '@customTypes/cart-items';
import HotelDetails from '@customTypes/hotel-details';
import { timeSlotProductSelected } from '@events/timeSlottedProduct';
import useAmplitudePayloads from '@hooks/useAmplitudePayloads';
import { useEvents } from '@events/EventsProvider';
import { SELECT_PRODUCT_CARD } from '@constants/amplitudeEvents';
import { productClicked } from '@events/DLP';
import useHotSpot from '@hooks/useHotSpot';
import iconPlus from '@assets/images/icon-plus.svg';
import { isProductUnAvailable } from '@helpers/product';
import Badge from '@components/SearchExperiment/HotelCard/Badge/Badge';
import TagIcon from '@assets/images/tag.svg';
import { useExperiment } from '@context/ExperimentContext';
import { hotelOffersTags } from '@constants/hotelOffers';
import ProductTagFactory from './ProductDetailsVariant/Components/ProductTagFactory';
import ProductDetailsPrice from './ProductDetailsPrice';

type ProductComponentWithImageProps = {
  products: Product[] | undefined;
  product: Product;
  date: Date | undefined;
  cartData: CartItemDetails | undefined;
  hotelDetails: HotelDetails;
  showFairPricing: boolean;
  setIsBottomDrawerOpen: Dispatch<SetStateAction<boolean>>;
};

export const getPhotoUrlFromProduct = (product: Product) => {
  if (product.is_product_time_group && product.children?.length) {
    return product.children?.[0].photo_url;
  }

  return product.photo_url;
};

export default function ProductComponentWithImage({
  products,
  product,
  date,
  cartData,
  hotelDetails,
  showFairPricing,
  setIsBottomDrawerOpen,
}: ProductComponentWithImageProps) {
  const { getTheTimeWithTheLowestPrice } = useHotSpot();
  const { track } = useEvents();
  const { generateHotelPayload, generateProductPayload } = useAmplitudePayloads();

  const hasProductPhotoUrl = useMemo(() => {
    const photoUrl = getPhotoUrlFromProduct(product)?.url;

    if (!photoUrl || photoUrl.includes('/null')) {
      return false;
    }

    return true;
  }, [product]);

  const shouldShowFairPricing = useMemo(
    () => !(date && isProductUnAvailable(product, cartData, hotelDetails, date)) && showFairPricing,
    [showFairPricing, date, product, cartData, hotelDetails],
  );

  const onCardClick = () => {
    setIsBottomDrawerOpen(true);

    // Event
    if (product.is_product_time_group && product.children) {
      timeSlotProductSelected(product);
    }

    const hotelPropertyGroup = generateHotelPayload(hotelDetails, products);
    const productPropertyGroup = generateProductPayload(product);

    track(SELECT_PRODUCT_CARD, {
      ...hotelPropertyGroup,
      has_offer: !!product.product_offer,
      offer_type: product.product_offer,
      products: [productPropertyGroup],
      is_photo: hasProductPhotoUrl,
    });

    productClicked(product, hotelDetails);
  };

  const { hotelOffersExperiment } = useExperiment();
  const showHotelOffers = hotelOffersExperiment === 'on';

  const hasProductOffer = !!product.product_offer && showHotelOffers;

  return (
    <button
      type="button"
      onClick={onCardClick}
      className="border border-rp-gray-divider cursor-pointer flex items-center justify-between outline-none pl-4 relative rounded-xl py-18.5px w-full h-116px"
    >
      <div className="flex flex-col w-full gap-1.5">
        <div className="flex items-center space-x-11px">
          <div className="flex flex-col items-start">
            <p className="font-rp-pn-semi-bold text-custom-black text-left text-md text-sm line-clamp-2 pr-4">
              {product.name}
            </p>
          </div>
        </div>

        <div className="flex flex-col space-y-1">
          <div className="flex flex-row flex-shrink-0 font-rp-pn-regular items-baseline leading-18px space-x-1 text-sm text-custom-black">
            {!date || product.is_product_time_group ? <p>Starting at</p> : null}

            <ProductDetailsPrice
              date={date}
              fairPricing={showFairPricing}
              hotelDetails={hotelDetails}
              personType="adult"
              product={
                product && product.is_product_time_group && product.children
                  ? getTheTimeWithTheLowestPrice(product.children, hotelDetails, cartData)
                  : product
              }
            />
          </div>

          <div className="flex flex-row flex-shrink-0 font-rp-pn-semi-bold items-baseline leading-4 space-x-1 text-xs text-rp-dark-gray-3">
            {shouldShowFairPricing ? <p>Includes fees</p> : null}

            {!product.individual_billing ? (
              <div className="flex flex-row space-x-1">
                {shouldShowFairPricing ? <p>•</p> : null}

                <p>Max {product.max_person_occupancy} People</p>
              </div>
            ) : null}
          </div>

          {hasProductOffer ? (
            <Badge
              tagContent={hotelOffersTags[product.product_offer!]?.shortTitle.replace(
                '[[AMOUNT]]',
                product.product_offer_amount || '',
              )}
              tagClass="bg-solar-surface-brand-ultramarine text-white"
              icon={<Image src={TagIcon} alt="tag-icon" />}
              upperCase={false}
            />
          ) : null}
        </div>
      </div>

      <div
        className={`h-116px ${
          hasProductPhotoUrl ? 'max-w-116px' : 'max-w-48px'
        } w-full relative flex`}
      >
        <div className="absolute -top-2 right-3 z-10">
          <ProductTagFactory
            date={date}
            cartData={cartData}
            hotelDetails={hotelDetails}
            product={product}
          />
        </div>
        {hasProductPhotoUrl && (
          <ImageLoaded
            url={getPhotoUrlFromProduct(product)?.url}
            width={116}
            classes="rounded-tr-lg rounded-br-lg"
            alt={product.name}
          />
        )}
        <div className="absolute z-10 right-2 bottom-0">
          <Image src={iconPlus} alt="add-icon" />
        </div>
      </div>
    </button>
  );
}
